import { useState, useEffect, useRef } from 'react';
import { api, API_BASE } from '../../utils/api';
import { Button, Card, Input, Select, Modal, Toggle, useToast } from '../shared/UI';

const COMMENT_OPTIONS = [
  { value: 'disabled', label: 'Disabled' },
  { value: 'optional', label: 'Optional' },
  { value: 'mandatory', label: 'Mandatory' },
];

const ROUTING_OPTIONS = [
  { value: 'specific', label: 'Specific Counters' },
  { value: 'general', label: 'Any Counter (General)' },
];

function ServiceForm({ initial, services, onSave, onCancel }) {
  const [form, setForm] = useState(initial || { name: '', parent_id: null, ticket_prefix: '', routing_type: 'specific', comment_mode: 'disabled', icon_path: '', is_open_ended: false });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Flat list of services for parent selection
  const flatServices = (list, depth = 0) => {
    let res = [];
    list.forEach(s => {
      if (s.id === initial?.id) return; // Cannot be own parent
      res.push({ value: s.id, label: `${'—'.repeat(depth)} ${s.name}` });
      if (s.children) res = [...res, ...flatServices(s.children, depth + 1)];
    });
    return res;
  };

  const parentOptions = [{ value: null, label: 'None (Top Level)' }, ...flatServices(services)];

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.upload('/api/media/upload', fd);
      set('icon_path', res.file_path);
    } catch {
      alert('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Input label="Service Name" value={form.name} onChange={v => set('name', v)} placeholder="e.g. Finance" required />
      <Select label="Parent Service" value={form.parent_id} onChange={v => set('parent_id', v)} options={parentOptions} />
      <Input label="Ticket Prefix" value={form.ticket_prefix} onChange={v => set('ticket_prefix', v.toUpperCase())} placeholder="e.g. FIN" />
      
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 10, border: '1px solid var(--gray-100)' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Open-ended Service</div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>Issue & close instantly (no queue)</div>
        </div>
        <Toggle checked={form.is_open_ended} onChange={v => set('is_open_ended', v)} />
      </div>

      {!form.is_open_ended && (
        <>
          <Select label="Routing Type" value={form.routing_type} onChange={v => set('routing_type', v)} options={ROUTING_OPTIONS} />
          <Select label="Agent Comment" value={form.comment_mode} onChange={v => set('comment_mode', v)} options={COMMENT_OPTIONS} />
        </>
      )}
      
      <div style={{ marginBottom: 8 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--gray-700)', marginBottom: 8 }}>Service Icon</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {form.icon_path ? (
            <img src={`${API_BASE}${form.icon_path}`} style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--gray-200)' }} />
          ) : (
            <div style={{ width: 48, height: 48, borderRadius: 8, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)', fontSize: 20 }}>📁</div>
          )}
          <Button size="sm" variant="ghost" onClick={() => fileRef.current.click()} loading={uploading}>
            {form.icon_path ? 'Change Icon' : 'Upload Icon'}
          </Button>
          <input ref={fileRef} type="file" style={{ display: 'none' }} accept="image/*" onChange={handleUpload} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <Button onClick={onCancel} variant="ghost" style={{ flex: 1 }}>Cancel</Button>
        <Button onClick={() => onSave(form)} style={{ flex: 1 }}>Save</Button>
      </div>
    </div>
  );
}

function ServiceNode({ node, depth = 0, onEdit, onToggle, onAddChild }) {
  const [expanded, setExpanded] = useState(depth < 1);

  return (
    <div className="service-tree-indent" style={{ marginLeft: depth * 24, borderLeft: depth > 0 ? '1px dashed var(--gray-200)' : 'none' }}>
      <div className="service-tree-node" style={{ 
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
        padding: '12px 16px', background: '#fff', borderRadius: 10, border: '1px solid var(--gray-100)', 
        marginBottom: 8, transition: '0.2s',
        opacity: node.is_active ? 1 : 0.6
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {node.children?.length > 0 && (
            <button onClick={() => setExpanded(!expanded)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--gray-400)', width: 20 }}>
              {expanded ? '▾' : '▸'}
            </button>
          )}
          {node.icon_path ? (
            <img src={`${API_BASE}${node.icon_path}`} style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 32, height: 32, borderRadius: 6, background: node.is_active ? 'var(--cobalt)' : 'var(--gray-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 800 }}>
              {node.ticket_prefix || node.name.slice(0,2).toUpperCase()}
            </div>
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 8 }}>
              {node.name}
              <span style={{ fontSize: 10, color: 'var(--gray-400)', fontFamily: 'var(--font-mono)' }}>{node.ticket_prefix}</span>
              {node.is_open_ended && (
                <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--cobalt)', background: 'rgba(66,165,245,0.1)', padding: '2px 6px', borderRadius: 4, letterSpacing: '0.05em' }}>OPEN-ENDED</span>
              )}
            </div>
            {depth === 0 && (
              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>
                {node.is_open_ended ? 'Auto-closed' : `${node.routing_type} · Comment: ${node.comment_mode}`}
              </div>
            )}
          </div>
        </div>
        <div className="service-tree-actions" style={{ display: 'flex', gap: 6 }}>
          {depth < 2 && <Button onClick={() => onAddChild(node)} variant="ghost" size="sm">+ Child</Button>}
          <Button onClick={() => onEdit(node)} variant="ghost" size="sm">Edit</Button>
          <Button onClick={() => onToggle(node)} variant={node.is_active ? 'danger' : 'success'} size="sm">
            {node.is_active ? '✕' : '✓'}
          </Button>
        </div>
      </div>
      {expanded && node.children?.map(child => (
        <ServiceNode key={child.id} node={child} depth={depth + 1} onEdit={onEdit} onToggle={onToggle} onAddChild={onAddChild} />
      ))}
    </div>
  );
}

export function ServicesPanel() {
  const [services, setServices] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { show, ToastContainer } = useToast();

  const load = () => api.get('/api/services/?tree=true').then(setServices);
  useEffect(() => { load(); }, []);

  const saveService = async (form) => {
    if (!form.name.trim()) return show('Name required', 'error');
    try {
      if (editing && editing.id) await api.put(`/api/services/${editing.id}`, form);
      else await api.post('/api/services/', form);
      show('Saved'); setModalOpen(false); setEditing(null); load();
    } catch (e) {
      show(e.error || 'Failed to save', 'error');
    }
  };

  const toggleService = async (s) => {
    await api.put(`/api/services/${s.id}`, { is_active: !s.is_active });
    show(`Service ${s.is_active ? 'disabled' : 'enabled'}`); load();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <ToastContainer />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: 'var(--navy)' }}>Services</h1>
          <p style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 14 }}>Manage hierarchical services (up to 3 levels)</p>
        </div>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }}>+ New Service</Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {services.length === 0 && <p style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>No services found. Create one to start.</p>}
        {services.map(s => (
          <ServiceNode key={s.id} node={s} 
            onEdit={setEditing} 
            onToggle={toggleService} 
            onAddChild={(p) => { setEditing({ parent_id: p.id }); setModalOpen(true); }}
          />
        ))}
      </div>

      <Modal open={modalOpen || !!(editing && editing.id)} onClose={() => { setModalOpen(false); setEditing(null); }} title={editing?.id ? 'Edit Service' : 'New Service'}>
        <ServiceForm
          initial={editing}
          services={services}
          onSave={saveService} 
          onCancel={() => { setModalOpen(false); setEditing(null); }} />
      </Modal>
    </div>
  );
}
